import {OverlayState} from "@/hooks/common/useOverlay";
import {appBottomScheme} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import * as style from "@COMPONENTS/style/productlist/FilterM";
import {TextButton} from "@FRAMEWORK/ui/mobile/button";
import {TextInput} from "@FRAMEWORK/ui/mobile/input";
import {Area} from "@FRAMEWORK/ui/mobile/layout";
import {PopupContainer} from "@FRAMEWORK/ui/mobile/popup";
import useGoodsFilter from "@QUERIES/prdt/goods/useGoodsFilter";
import {format} from "@yb/utils";
import dayjs from "dayjs";
import {useRouter} from "next/router";
import React, {useCallback, useMemo, useState} from "react";
import useFilterStore from "../../../common/filterHook";
import DepartureAirport from "./DepartureAirport";
import DepartureDate from "./DepartureDate";
import DepartureDay from "./DepartureDay";
import DepartureTime from "./DepartureTime";
import ProductFeature from "./ProductFeature";
import Promotion from "./Promotion";
import Transportation from "./Transportation";
import TravelBudget from "./TravelBudget";
import TravelDays from "./TravelDays";
import TravelMember from "./TravelMember";

type Filters = {
	outStartDate: {value: string; text: string}; // 출발 일정
	weekDayList: string[]; // 출발 요일
	timeList: string[]; // 출발 시간
	airportList: string[]; // 출발지(공항)
	travelDays: string[]; // 여행 기간
	minMaxPrice: {value: string; text: string};
	transportation: {code: string; name: string}[]; // 이용 교통
	productFeature: {code: string; name: string; divi: string}[]; // 상품 특징
	promotion: {code: string; name: string}[]; // 프로모션
	travelMember: {code: string; name: string}[]; // 여행구성원
};

// Array filter 관련
export const FILTER_ALL = "ALL";
const getInitialFilters = (
	filters: {
		key: string;
		text: string;
		value: string;
		asc?: boolean;
	}[],
) => {
	const outStartDate = filters
		.filter(e => e.key === "outStartDateList")
		.map(t => ({value: t.value, text: t.text}))[0] ?? {value: "", text: ""};
	const weekDayList = filters.filter(e => e.key === "wdayList").map(t => t.value) ?? [];
	const timeList = filters.filter(e => e.key === "outDeprtFgList").map(t => t.value) ?? [];
	const airportList = filters.filter(e => e.key === "outCityDiviNm").map(t => t.value) ?? [];
	const travelDays = filters.filter(e => e.key === "evDayCntList").map(t => t.text) ?? [];
	const minMaxPriceValue =
		filters
			.filter(e => e.key === "minPrice" || e.key === "maxPrice")
			.map(o => o.value)
			.join(",") ?? "";
	const minMaxPriceText =
		filters
			.filter(e => e.key === "minPrice" || e.key === "maxPrice")
			.map(t => `₩${t.text}`)
			.join(" ~ ") ?? "";
	const transportation = filters.filter(e => e.key === "airCd").map(t => ({code: t.value, name: t.text})) ?? [];
	const productFeature =
		filters.filter(e => e.key === "goodsFeaturesList").map(t => ({code: t.value, name: t.text, divi: ""})) ?? [];
	const promotion = filters.filter(e => e.key === "goodsPromotion").map(t => ({code: t.value, name: t.text})) ?? [];
	const travelMember = filters.filter(e => e.key === "goodsTmember").map(t => ({code: t.value, name: t.text})) ?? [];

	return {
		outStartDate,
		weekDayList,
		timeList,
		airportList,
		travelDays,
		minMaxPrice: {
			value: minMaxPriceValue,
			text: minMaxPriceText,
		},
		transportation,
		productFeature,
		promotion,
		travelMember,
	};
};
const getBlankFilters = () => ({
	outStartDate: {value: "", text: ""},
	weekDayList: [] as string[], // 출발 요일
	timeList: [] as string[], // 출발 시간
	airportList: [] as string[], // 출발지(공항)
	travelDays: [] as string[], // 여행 기간
	minMaxPrice: {value: "", text: ""},
	transportation: [] as {code: string; name: string}[], // 이용 교통
	productFeature: [] as {code: string; name: string; divi: string}[], // 상품 특징
	promotion: [] as {code: string; name: string}[], // 프로모션
	travelMember: [] as {code: string; name: string}[], // 여행구성원
});
const OBJECT_ARRAY_FILTER_LIST = ["transportation", "productFeature", "promotion", "travelMember"];
const setArrayFilter = (
	checked: boolean,
	value: string,
	stateName: keyof Filters,
	setState: React.Dispatch<React.SetStateAction<Filters>>,
) => {
	let val: string | {code: string; name: string; divi?: string} = value;
	if (OBJECT_ARRAY_FILTER_LIST.includes(stateName)) {
		val = JSON.parse(value);
	}

	if (checked) {
		// 사용자가 "전체"를 골랐을 경우, default로 설정
		if (val === "전체" || (typeof val === "object" && val.code === FILTER_ALL)) {
			return setState(prev => ({...prev, [stateName]: []}));
		}
		setState(prev => ({...prev, [stateName]: [...(prev[stateName] as []), val]}));
	} else {
		// Object Array를 삭제할 경우
		if (typeof val === "object") {
			return setState(prev => ({
				...prev,
				[stateName]: (prev[stateName] as []).filter(
					(el: {code: string; name: string; divi?: string}) =>
						el.code !== (val as {code: string; name: string; divi?: string}).code,
				),
			}));
		}
		// String Array를 삭제할경우
		setState(prev => ({...prev, [stateName]: (prev[stateName] as []).filter(el => el !== val)}));
	}
};
// String filter 관련
const setObjectFilter = (
	value: string,
	text: string,
	stateName: keyof Filters,
	setState: React.Dispatch<React.SetStateAction<Filters>>,
) => {
	setState(prev => ({...prev, [stateName]: {value, text}}));
};

const LocalDetailFilterComponent = (overlayProps: OverlayState) => {
	const router = useRouter();
	const {getFilter, setFilter} = useFilterStore();
	const {data: filterData} = useGoodsFilter(router.query.dspSid as string);
	const filter = filterData.body;

	const [activeIndex, setActiveIndex] = useState(0);
	const toggleAccordion = (index: number) => {
		if (activeIndex === index) {
			setActiveIndex(null);
		} else {
			setActiveIndex(index);
		}
	};

	const filters = getFilter() || [];
	const [searchGoods, setSearchGoods] = useState({goodsNm: filters.find(e => e.key === "goodsNm")?.value ?? ""});
	const [price, setPrice] = useState({
		minPrice: filters.find(e => e.key === "minPrice")?.value ?? "",
		maxPrice: filters.find(e => e.key === "maxPrice")?.value ?? "",
	});
	const [filterValues, setFilterValues] = useState(getInitialFilters(filters));

	const reassignedFilters = useMemo(
		() =>
			Object.keys(filterValues).map(stateName => ({
				stateName,
				state: filterValues[stateName],
				setState: value => setFilterValues(prev => ({...prev, [stateName]: value})),
			})),
		[filterValues],
	);

	const hasFilter = useMemo(() => {
		const checkArray = [];
		for (const key in filterValues) {
			const value = filterValues[key];
			checkArray.push(Array.isArray(value) ? !!value.length : !!value.value);
		}

		return checkArray.includes(true);
	}, [filterValues]);

	const clearFilters = () => {
		setFilterValues(getBlankFilters());
	};

	// 출발 일정 관련
	const callAlertPopup = (callback: () => void) => {
		MessageUtil.confirm(
			"출발 일정 조건을 변경하시는 경우<br/>기존 설정하신 출발 일정은 초기화 됩니다.<br/>출발 일정 조건을 변경하시겠습니까?",
			callback,
		).then(() => {
			appBottomScheme(false);
		});
	};
	const handleMonthList = useCallback(
		(value: string) => {
			let formatted = "";
			if (value === FILTER_ALL) {
				formatted = "출발 월 전체";
			} else {
				formatted = dayjs(value.toString()).format("YYYY년 MM월 출발");
			}
			if (filterValues.outStartDate.value.length === 8) {
				callAlertPopup(() => setObjectFilter(value, formatted, "outStartDate", setFilterValues));
			} else {
				setObjectFilter(value, formatted, "outStartDate", setFilterValues);
			}
		},
		[filterValues.outStartDate.value],
	);
	const removeOutStartDate = () => {
		setFilterValues(prev => ({...prev, outStartDate: {value: "", text: ""}}));
	};
	const handleCalendar = (date: string) => {
		const text = `${dayjs(date).format("YYYY.MM.DD")} 출발`;
		const wday = filterValues.weekDayList[0];
		if (filterValues.outStartDate.value.length === 6 || !!wday) {
			return callAlertPopup(() => {
				// 출발 요일이 선택된 상태일 경우 초기화
				setFilterValues(prev => ({...prev, weekDayList: []}));
				setFilterValues(prev => ({...prev, outStartDate: {value: date, text}}));
			});
		}
		if (!date) {
			return removeOutStartDate();
		}

		setFilterValues(prev => ({...prev, outStartDate: {value: date, text}}));
	};

	// 출발 요일 관련
	const handleDepartureDay = useCallback(
		(checked: boolean, value: string) => {
			if (filterValues.outStartDate.value.length === 8) {
				return callAlertPopup(() => {
					// 출발일정 중 상세일이 선택된 경우 초기화
					setFilterValues(prev => ({...prev, outStartDate: {value: "", text: ""}}));
					setArrayFilter(checked, value, "weekDayList", setFilterValues);
				});
			}
			setArrayFilter(checked, value, "weekDayList", setFilterValues);
		},
		[filterValues.outStartDate],
	);

	// 여행 예산 관련
	const handlePriceText = (minPrice: string, maxPrice: string) => {
		const goodsMinPrice = Number(filter.minPrice);
		const goodsMaxPrice = Number(filter.maxPrice);
		const searchMinPrice = Number(minPrice);
		const searchMaxPrice = Number(maxPrice);
		const resultMinPrice = () => {
			if (searchMinPrice < goodsMinPrice) {
				return goodsMinPrice;
			} else if (searchMinPrice > goodsMaxPrice) {
				return goodsMaxPrice;
			} else {
				return searchMinPrice;
			}
		};
		const resultMaxPrice = () => {
			if (searchMaxPrice > goodsMaxPrice) {
				return goodsMaxPrice;
			} else if (searchMaxPrice < goodsMinPrice) {
				return goodsMinPrice;
			} else {
				return searchMaxPrice;
			}
		};
		setPrice({minPrice: resultMinPrice().toString(), maxPrice: resultMaxPrice().toString()});
		setFilterValues(prev => ({
			...prev,
			minMaxPrice: {
				value: `${resultMinPrice()},${resultMaxPrice()}`,
				text: `₩${format.koKRFormatNumber(resultMinPrice())} ~ ₩${format.koKRFormatNumber(resultMaxPrice())}`,
			},
		}));
	};

	// State, filterstore Sync
	const handleSubmit = useCallback(() => {
		const submitFilters: {key: string; value: string; text: string}[] = [];
		// sortOrder
		submitFilters.push(getFilter().find(v => v.key === "sort"));
		// 상세 검색
		!!searchGoods.goodsNm &&
			submitFilters.push({key: "goodsNm", value: searchGoods.goodsNm, text: searchGoods.goodsNm});
		// 출발 일정
		!!filterValues.outStartDate.value &&
			submitFilters.push({
				key: "outStartDateList",
				value: filterValues.outStartDate.value,
				text: filterValues.outStartDate.text,
			});
		// 출발 요일
		!!filterValues.weekDayList.length &&
			filterValues.weekDayList.forEach(wday =>
				submitFilters.push({key: "wdayList", value: wday, text: `${wday}요일 출발`}),
			);
		// 출발 시간
		!!filterValues.timeList.length &&
			filterValues.timeList.forEach(outDeprtFg =>
				submitFilters.push({key: "outDeprtFgList", value: outDeprtFg, text: `${outDeprtFg} 출발`}),
			);
		// 출발지(공항)
		!!filterValues.airportList.length &&
			filterValues.airportList.forEach(outCityDiviNm =>
				submitFilters.push({key: "outCityDiviNm", value: outCityDiviNm, text: `${outCityDiviNm} 출발`}),
			);
		// 여행 기간
		!!filterValues.travelDays.length &&
			filterValues.travelDays.forEach(evDayCnt =>
				submitFilters.push({key: "evDayCntList", value: evDayCnt.replace("일", ""), text: evDayCnt}),
			);
		// 여행 예산
		const minMaxPrice = filterValues.minMaxPrice.value ? (filterValues.minMaxPrice.value as string).split(",") : [];
		// 여행 예산(최소)
		minMaxPrice.length > 1 &&
			submitFilters.push({
				key: "minPrice",
				value: minMaxPrice[0],
				text: `${format.koKRFormatNumber(minMaxPrice[0])}원`,
			});
		// 여행 예산(최대)
		minMaxPrice.length > 1 &&
			submitFilters.push({
				key: "maxPrice",
				value: minMaxPrice[1],
				text: `${format.koKRFormatNumber(minMaxPrice[1])}원`,
			});
		// 이용교통
		!!filterValues.transportation.length &&
			filterValues.transportation.forEach(transport =>
				submitFilters.push({key: "airCd", value: transport.code, text: transport.name}),
			);
		// 상품특징
		!!filterValues.productFeature.length &&
			filterValues.productFeature.forEach(feature =>
				submitFilters.push({key: "goodsFeaturesList", value: feature.code, text: feature.name}),
			);
		// 프로모션
		!!filterValues.promotion.length &&
			filterValues.promotion.forEach(promo =>
				submitFilters.push({key: "goodsPromotion", value: promo.code, text: promo.name}),
			);
		// 여행구성원
		!!filterValues.travelMember.length &&
			filterValues.travelMember.forEach(member =>
				submitFilters.push({key: "goodsTmember", value: member.code, text: member.name}),
			);

		setFilter(submitFilters);

		overlayProps.close();
	}, [searchGoods, price, filterValues]);

	const getFilterList = () => {
		const filterComponents: {title: string; Component: () => JSX.Element}[] = [
			{
				title: "출발일정",
				Component: () => (
					<DepartureDate
						dataStr={filter.monList}
						handleMonth={handleMonthList}
						outStartDate={filterValues.outStartDate}
						removeOutStartDate={removeOutStartDate}
						handleCalendar={handleCalendar}
					/>
				),
			},
			{
				title: "출발 요일",
				Component: () => (
					<DepartureDay
						tagList={filterValues.weekDayList}
						handleTagCheck={handleDepartureDay}
						dataStr={filter.wdayList}
					/>
				),
			},
			{
				title: "출발 시간",
				Component: () => (
					<DepartureTime
						tagList={filterValues.timeList}
						handleTagCheck={(checked, value) => setArrayFilter(checked, value, "timeList", setFilterValues)}
						dataStr={filter.outDeprtFgList}
					/>
				),
			},
			{
				title: "출발지 (공항)",
				Component: () => (
					<DepartureAirport
						tagList={filterValues.airportList}
						handleTagCheck={(checked, value) => setArrayFilter(checked, value, "airportList", setFilterValues)}
						dataStr={filter.outCityList}
					/>
				),
			},
			{
				title: "여행 기간",
				Component: () => (
					<TravelDays
						tagList={filterValues.travelDays}
						handleTagCheck={(checked, value) => setArrayFilter(checked, value, "travelDays", setFilterValues)}
						dataStr={filter.evNightList}
					/>
				),
			},
			{
				title: "여행 예산 (1인 기준)",
				Component: () => (
					<TravelBudget
						initialPrice={price}
						minPricePlaceholder={filter.minPrice}
						maxPricePlaceholder={filter.maxPrice}
						handlePriceText={handlePriceText}
					/>
				),
			},
			{
				title: "이용 교통",
				Component: () => (
					<Transportation
						tagList={filterValues.transportation}
						handleTagCheck={(checked, value) => setArrayFilter(checked, value, "transportation", setFilterValues)}
						dataStr={filter.airList}
					/>
				),
			},
		];
		filter.featuresList &&
			filterComponents.push({
				title: "상품 특징",
				Component: () => (
					<ProductFeature
						tagList={filterValues.productFeature}
						handleTagCheck={(checked, value) => setArrayFilter(checked, value, "productFeature", setFilterValues)}
						dataStr={filter.featuresList}
					/>
				),
			});
		filter.promotionList &&
			filterComponents.push({
				title: "프로모션",
				Component: () => (
					<Promotion
						tagList={filterValues.promotion}
						handleTagCheck={(checked, value) => setArrayFilter(checked, value, "promotion", setFilterValues)}
						dataStr={filter.promotionList}
					/>
				),
			});
		filter.tmemberList &&
			filterComponents.push({
				title: "여행구성원",
				Component: () => (
					<TravelMember
						tagList={filterValues.travelMember}
						handleTagCheck={(checked, value) => setArrayFilter(checked, value, "travelMember", setFilterValues)}
						dataStr={filter.tmemberList}
					/>
				),
			});

		return filterComponents;
	};

	// 위치 고정
	if (!filters) return <></>;
	return (
		<PopupContainer {...overlayProps} title="필터" size="full" animate="slideIn" padding={false}>
			<style.Wrap>
				{/* 검색창 */}
				<Area>
					<style.ContentWrap>
						<TextInput
							placeholder="상품 상세 검색"
							onChange={e => setSearchGoods({goodsNm: e.target.value})}
							value={searchGoods.goodsNm}
						/>
					</style.ContentWrap>
				</Area>
				{/* 카테고리별 필터 */}
				<style.AccordionArea>
					{getFilterList().map(({title, Component}, index) => (
						<style.AccordionItem key={index} isToggle={activeIndex === index}>
							<style.AccordionContent onClick={() => toggleAccordion(index)} isToggle={activeIndex === index}>
								<style.AccordionTitle>{title}</style.AccordionTitle>
								<style.AccordionMoreButton isToggle={activeIndex === index}>
									<span>더보기</span>
								</style.AccordionMoreButton>
							</style.AccordionContent>
							<style.AccordionDetail isActive={activeIndex === index}>
								<Component />
							</style.AccordionDetail>
						</style.AccordionItem>
					))}
				</style.AccordionArea>
				{/* 필터 요약 */}
				<style.FloatArea>
					{hasFilter && (
						<style.FilterTagBox>
							<span>필터</span>
							<button onClick={clearFilters}>전체삭제</button>
							<style.TagList>
								{/* 상세 검색 */}
								{!!searchGoods.goodsNm && (
									<style.TagListItem>
										{searchGoods.goodsNm}
										<button onClick={() => setSearchGoods({goodsNm: ""})}>
											<span>닫기</span>
										</button>
									</style.TagListItem>
								)}
								{reassignedFilters.map(({stateName, state, setState}, filterIdx) => (
									<React.Fragment key={filterIdx}>
										{Array.isArray(state)
											? // 배열 데이터
											  state.map((tag, stateIdx) => (
													<style.TagListItem key={stateIdx}>
														{!OBJECT_ARRAY_FILTER_LIST.includes(stateName) ? tag : tag.name}
														<button onClick={() => setState(state.filter(e => e !== tag))}>
															<span>닫기</span>
														</button>
													</style.TagListItem>
											  ))
											: // 오브젝트 데이터
											  !!state.value && (
													<style.TagListItem>
														{state.text}
														<button onClick={() => setState({value: "", text: ""})}>
															<span>닫기</span>
														</button>
													</style.TagListItem>
											  )}
									</React.Fragment>
								))}
							</style.TagList>
						</style.FilterTagBox>
					)}
					<TextButton types="confirm" size="submit" text="검색하기" onClick={handleSubmit} />
				</style.FloatArea>
			</style.Wrap>
		</PopupContainer>
	);
};

const LocalDetailFilter = (props: OverlayState) => (
	<ErrorBoundary logMsg="DetailFilter Error">
		<LocalDetailFilterComponent {...props} />
	</ErrorBoundary>
);

export default LocalDetailFilter;
